import React from 'react';
import type { ScanCountSerial } from '@/domain/warehouse';

type SerialChipsProps = {
  lineName: string;
  serials: readonly ScanCountSerial[];
  /** Units in this count, listed or added by a lookup. */
  countedIds: readonly string[];
  /** Codes of counted units the line does not list, by serial id. */
  unlisted: { [serialId: string]: string };
  /** Count mode: chips are toggles (aria-pressed). Scan mode shows them as status only. */
  interactive: boolean;
  /** The line has no room left, so uncounted chips cannot be pressed. */
  full: boolean;
  disabled?: boolean;
  /** "received" or "picked". */
  donePast: string;
  onToggle: (serialId: string) => void;
};

/** A serial line's units: done earlier (solid, fixed), in this count, or still due (dashed). */
export default function SerialChips({
  lineName,
  serials,
  countedIds,
  unlisted,
  interactive,
  full,
  disabled = false,
  donePast,
  onToggle,
}: SerialChipsProps) {
  const counted = new Set(countedIds);
  const extraIds = countedIds.filter((id) => unlisted[id] !== undefined);
  if (!serials.length && !extraIds.length) return null;
  const doneTitle = `${donePast.charAt(0).toUpperCase()}${donePast.slice(1)} earlier`;

  return (
    <ul className="scan-count-chips" aria-label={`Units on ${lineName}`}>
      {serials.map((serial) => {
        if (serial.done) {
          return (
            <li key={serial.id}>
              <span className="scan-count-chip" data-state="done" title={serial.label || doneTitle}>
                {serial.code}
                <span className="sr-only">, {donePast} earlier</span>
              </span>
            </li>
          );
        }
        const isCounted = counted.has(serial.id);
        if (!interactive) {
          return (
            <li key={serial.id}>
              <span className="scan-count-chip" data-state={isCounted ? 'counted' : 'todo'} title={serial.label || undefined}>
                {serial.code}
                <span className="sr-only">{isCounted ? ', counted' : ', still due'}</span>
              </span>
            </li>
          );
        }
        return (
          <li key={serial.id}>
            <button
              type="button"
              className="scan-count-chip"
              data-state={isCounted ? 'counted' : 'todo'}
              aria-pressed={isCounted}
              disabled={disabled || (!isCounted && full)}
              title={serial.label || undefined}
              onClick={() => onToggle(serial.id)}
            >
              {serial.code}
            </button>
          </li>
        );
      })}
      {extraIds.map((id) => (
        <li key={id}>
          {interactive ? (
            <button
              type="button"
              className="scan-count-chip"
              data-state="counted"
              data-extra="true"
              aria-pressed
              aria-label={`${unlisted[id]}, added by a scan`}
              disabled={disabled}
              onClick={() => onToggle(id)}
            >
              {unlisted[id]}
            </button>
          ) : (
            <span className="scan-count-chip" data-state="counted" data-extra="true">
              {unlisted[id]}
              <span className="sr-only">, counted, added by a scan</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
